/**
 * usePresets — the access-preset catalogue (GET /presets; shared-auth step 7,
 * ADR-061) for the binding pickers in Settings ▸ Members / Groups / Spaces.
 *
 * Read once per session through a module-level cached promise, so the three
 * tabs share one request. FAIL LOUD by design: there is no built-in fallback
 * list — a mirror is exactly the hand-maintained copy step 7 deletes, and a
 * picker drawing on stale scopes would write grants the server refuses (or,
 * worse, accepts). On error `presets` stays null and `error` carries the
 * message; the pickers render disabled with it visible. A failed read is not
 * cached, so the next mount retries.
 */

import { useEffect, useState } from 'react';
import { problemDetail, tenantApi, type Preset } from '@/services/tenantApi';

let cache: Promise<Preset[]> | null = null;

/** The catalogue, one request shared by every caller; a rejection clears the
 *  cache so the next call retries instead of replaying the failure. */
export function loadPresets(): Promise<Preset[]> {
  if (!cache) {
    cache = tenantApi.listPresets().catch((err: unknown) => {
      cache = null;
      throw err;
    });
  }
  return cache;
}

/** Why a catalogue read failed, as a sentence: the problem detail of an HTTP
 *  error, else the thrown message (a malformed response), else a fallback. */
function failureMessage(err: unknown): string {
  const fallback =
    err instanceof Error && err.message ? err.message : 'Could not load the access presets.';
  return problemDetail(err, fallback);
}

export function usePresets(): { presets: Preset[] | null; error: string | null } {
  const [presets, setPresets] = useState<Preset[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadPresets().then(
      (list) => {
        if (!cancelled) setPresets(list);
      },
      (err: unknown) => {
        if (!cancelled) setError(failureMessage(err));
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  return { presets, error };
}
